import React, { useEffect, useState } from 'react';
import { CheckCheck, Check, Loader2 } from 'lucide-react';
import { ChatMessage } from '../../../shared/types.ts';
import { Avatar } from '../common/Avatar.tsx';
import { Modal } from '../common/Modal.tsx';

interface MemberInfo {
  userId: string;
  userName: string;
  avatarUrl?: string | null;
}

interface ReadInfo extends MemberInfo {
  readAt: string;
}

interface MessageInfo {
  readBy: ReadInfo[];
  pending: MemberInfo[];
  recipientCount: number;
  isSeen: boolean;
}

interface MessageInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: ChatMessage | null;
  token: string | null;
  currentUserId?: string;
}

// "Today, 4:15 PM" / "Yesterday, 9:02 AM" / "21 Oct, 11:40 AM"
const formatReadTime = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(value.includes(' ') && !value.includes('T') ? value.replace(' ', 'T') : value);
  if (isNaN(d.getTime())) return value;
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);
  if (dayDiff === 0) return `Today, ${time}`;
  if (dayDiff === 1) return `Yesterday, ${time}`;
  return `${d.toLocaleDateString([], { day: 'numeric', month: 'short' })}, ${time}`;
};

export const MessageInfoModal: React.FC<MessageInfoModalProps> = ({
  isOpen,
  onClose,
  message,
  token,
  currentUserId,
}) => {
  const [info, setInfo] = useState<MessageInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Always load fresh when opened, so members who read it a moment ago are included
  useEffect(() => {
    if (!isOpen || !message || !token) return;
    let cancelled = false;
    setInfo(null);
    setError(null);
    setIsLoading(true);

    fetch(`/api/chat/messages/${message.id}/info`, {
      headers: { Authorization: `Bearer ${token}`, ...(currentUserId ? { 'x-demo-user-id': currentUserId } : {}) },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) setError(data.error || `Could not load message info (server responded ${res.status}).`);
        else setInfo(data);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load message info.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, message?.id, token]);

  if (!isOpen || !message) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Message info">
      <div className="space-y-4 text-xs">
        {/* The message being inspected */}
        <div className="p-3 rounded-xl bg-brand-50/60 dark:bg-brand-950/30 border border-brand-200/60 dark:border-brand-800/50">
          <p className="whitespace-pre-wrap break-words text-surface-900 dark:text-surface-100 line-clamp-4">
            {message.content}
          </p>
          {message.attachments && message.attachments.length > 0 && (
            <span className="text-[10px] font-semibold text-brand-600 dark:text-brand-400 mt-1 block">
              + {message.attachments.length} attachment(s)
            </span>
          )}
          <span className="text-[10px] text-surface-500 dark:text-surface-400 mt-1.5 block text-right">
            Sent {formatReadTime(message.createdAt)}
          </span>
        </div>

        {isLoading && (
          <div className="py-6 flex justify-center text-surface-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}

        {error && <div className="py-3 text-center text-rose-500">{error}</div>}

        {info && (
          <>
            {/* Read by */}
            <div>
              <div className="flex items-center space-x-1.5 mb-1.5 text-[11px] font-bold uppercase tracking-wider text-[#53bdeb]">
                <CheckCheck className="w-3.5 h-3.5 stroke-[2.5]" />
                <span>
                  Read by {info.readBy.length} of {info.recipientCount}
                </span>
              </div>
              {info.readBy.length === 0 ? (
                <div className="py-2 text-surface-400 italic">No one has read this message yet.</div>
              ) : (
                <ul className="divide-y divide-surface-100 dark:divide-surface-800">
                  {info.readBy.map((r) => (
                    <li key={r.userId} className="flex items-center justify-between py-2">
                      <div className="flex items-center space-x-2.5 min-w-0">
                        <Avatar src={r.avatarUrl} name={r.userName} size="sm" userId={r.userId} />
                        <span className="font-semibold text-surface-900 dark:text-surface-100 truncate">
                          {r.userName}
                        </span>
                      </div>
                      <span className="text-surface-500 dark:text-surface-400 shrink-0 ml-3">
                        {formatReadTime(r.readAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Not read yet */}
            {info.pending.length > 0 && (
              <div>
                <div className="flex items-center space-x-1.5 mb-1.5 text-[11px] font-bold uppercase tracking-wider text-surface-400">
                  <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                  <span>Delivered, not read yet ({info.pending.length})</span>
                </div>
                <ul className="divide-y divide-surface-100 dark:divide-surface-800">
                  {info.pending.map((p) => (
                    <li key={p.userId} className="flex items-center py-2">
                      <div className="flex items-center space-x-2.5 min-w-0">
                        <Avatar src={p.avatarUrl} name={p.userName} size="sm" userId={p.userId} />
                        <span className="font-semibold text-surface-700 dark:text-surface-300 truncate">
                          {p.userName}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};
